export type SecurityAuthorityCertificationStatus =
  | 'PASS'
  | 'FAIL'
  | 'UNVERIFIED';

export interface SecurityAuthorityActorEvidence {
  actorClass:
    | 'TREE_OWNER'
    | 'CLAIMED_SAME_TREE_MEMBER'
    | 'VERIFIED_UNRELATED_USER';
  accountProfilesVisible: number;
  familyProfileCardsVisible: number;
  familyPhotosAndVideosVisible: number;
  expectedFamilyAccess: boolean;
  certificationStatus: SecurityAuthorityCertificationStatus;
  explanation: string;
}

export interface SecurityAuthorityEvidenceProjection {
  title: string;
  subsystem: 'security';
  authorityDomain: 'family-profile-media-memory';
  status: SecurityAuthorityCertificationStatus;
  evidenceSource: 'live-database-role-certification';
  migration: string;
  certification: string;
  correctedIdentitySelection: boolean;
  membershipIndependentFromFamilyRelationship: boolean;
  familyMemberRows: number;
  familyPhotoVideoRows: number;
  claimedSameTreeUsers: number;
  privateAccountProfilesSelfOnly: boolean;
  unrelatedFamilyAccessDenied: boolean;
  sameTreeFamilyAccessPreserved: boolean;
  constitutionalRule: string;
  actors: SecurityAuthorityActorEvidence[];
  verifiedAt: string;
}

export function projectSecurityAuthorityEvidence():
  SecurityAuthorityEvidenceProjection {
  return {
    title: 'Family Profile, Media, and Memory Authority',
    subsystem: 'security',
    authorityDomain: 'family-profile-media-memory',
    status: 'PASS',
    evidenceSource: 'live-database-role-certification',
    migration:
      'mae_001c_family_profile_media_memory_authority',
    certification:
      'MAE-001D corrected live role-based authority certification',
    correctedIdentitySelection: true,
    membershipIndependentFromFamilyRelationship: true,
    familyMemberRows: 52,
    familyPhotoVideoRows: 12,
    claimedSameTreeUsers: 2,
    privateAccountProfilesSelfOnly: true,
    unrelatedFamilyAccessDenied: true,
    sameTreeFamilyAccessPreserved: true,
    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    actors: [
      {
        actorClass: 'TREE_OWNER',
        accountProfilesVisible: 1,
        familyProfileCardsVisible: 52,
        familyPhotosAndVideosVisible: 12,
        expectedFamilyAccess: true,
        certificationStatus: 'PASS',
        explanation:
          'The tree owner retains private self-profile access and complete visibility into the authorized family tree.',
      },
      {
        actorClass: 'CLAIMED_SAME_TREE_MEMBER',
        accountProfilesVisible: 1,
        familyProfileCardsVisible: 52,
        familyPhotosAndVideosVisible: 12,
        expectedFamilyAccess: true,
        certificationStatus: 'PASS',
        explanation:
          'A claimed relative retains complete same-tree access even when that relative purchases and owns a separate membership.',
      },
      {
        actorClass: 'VERIFIED_UNRELATED_USER',
        accountProfilesVisible: 1,
        familyProfileCardsVisible: 0,
        familyPhotosAndVideosVisible: 0,
        expectedFamilyAccess: false,
        certificationStatus: 'PASS',
        explanation:
          'The test identity had no active claimed profile in the tree and was denied access to that family’s cards and media.',
      },
    ],
    verifiedAt: '2026-07-16',
  };
}
